import WebApp from '@twa-dev/sdk';
import { type AxiosError, type AxiosResponse, isAxiosError } from 'axios';

import { getLocale } from '@/app/lib/localization/helpers/get-locale';

import { ApiService } from '@/shared/api/ApiService';
import { getDeviceInfo } from '@/shared/helpers/environment/get-device-info';

import {
  LogDevMessageKinds,
  type MessageOrGetter,
  type ServiceLoggerOptions,
} from './types';

import { version as appVersion } from '../../../../package.json';

export class BaseLogger {
  static readonly info = this.makeLogFunction(
    LogDevMessageKinds.info,
    (text: string) => text,
    ['info', (event) => event]
  );

  protected static readonly COLOR_BY_MESSAGE_KIND: Record<
    LogDevMessageKinds,
    `#${string}`
  > = {
    [LogDevMessageKinds.analytics]: '#03a9f4',
    [LogDevMessageKinds.api]: '#fc5fd5',
    [LogDevMessageKinds.retry]: '#ff9500',
    [LogDevMessageKinds.apiError]: '#ff3b30',
    [LogDevMessageKinds.error]: '#d10000',
    [LogDevMessageKinds.ws]: '#2cc153',
    [LogDevMessageKinds.info]: '#f3ce49',
    [LogDevMessageKinds.online]: '#2cc153',
    [LogDevMessageKinds.offline]: '#ff3b30',
  };

  protected static readonly LIB_NAME = 'WRAPPER';

  protected static predefinedOptions: ServiceLoggerOptions = {} as ServiceLoggerOptions;

  public static async init() {
    const telegram_id = WebApp?.initDataUnsafe?.user?.id;
    const deviceInfo = getDeviceInfo(WebApp.platform);
    const locale = await getLocale();

    this.predefinedOptions = {
      d_mod: deviceInfo.device_model,
      d_os: deviceInfo.device_os,
      d_p: deviceInfo.device_platform,
      loc_app: locale.app,
      user_lang: locale.game,
      tg_id: String(telegram_id),
      lib_n: this.LIB_NAME,
      lib_v: appVersion,
    };
  }

  static makeLogDevMessage<Message, Arguments extends unknown | undefined = undefined>(
    kind: LogDevMessageKinds,
    header: MessageOrGetter<Message, Arguments, string>,
    ...content: [string, MessageOrGetter<Message, Arguments, any>][]
  ) {
    const formatEventMessage = this.makeFormatEventMessage(kind);

    return (message: Message, ...args: Arguments[]) => {
      const messageGroups = formatEventMessage(
        this.normalizeToValue(header, message, ...args)
      );

      console.groupCollapsed(...messageGroups);

      content.forEach(([key, item]) => {
        console.log(key, this.normalizeToValue(item, message, ...args));
      });

      console.groupEnd();
    };
  }

  protected static formatTitle(response: AxiosResponse | AxiosError) {
    const baseUrl =
      ApiService.instance.defaults.baseURL ?? import.meta.env.VITE_APP_API_URL;

    let res =
      (response.request?.responseURL ?? '').replace(baseUrl, '') ||
      (response.config?.url ?? '');

    if (baseUrl.includes('test')) {
      res = `TEST ${res}`;
    }

    return res;
  }

  protected static getErrorMessage(err: unknown): string {
    if (typeof err === 'string') {
      return err;
    }

    if (isAxiosError(err)) {
      return `msg: ${err?.message}; code: ${err?.code}; status: ${err?.status}`;
    }

    const e = err && typeof err === 'object' ? (err as Record<string, any>) : {};
    const error = e.reason || e.error;
    return error?.toString?.() || e.message || e.reason || 'Unknown error';
  }

  protected static makeFormatEventMessage(kind: LogDevMessageKinds) {
    return <Content>(...content: Content[]) => {
      const headerCss = [
        `color: ${this.COLOR_BY_MESSAGE_KIND[kind]}`,
        ...content.map(() => 'color: inherit;'),
      ];

      const parts = [`%c${kind}`, ...content.map((item) => `%c${item}`)];

      return [parts.join(' '), ...headerCss];
    };
  }

  protected static makeLogFunction<
    Message,
    Arguments extends unknown | undefined = undefined,
  >(
    kind: LogDevMessageKinds,
    header: MessageOrGetter<Message, Arguments, string>,
    ...content: [string, MessageOrGetter<Message, Arguments, any>][]
  ) {
    const formatEventMessage = this.makeFormatEventMessage(kind);

    return (message: Message, ...args: Arguments[]) => {
      const messageGroups = formatEventMessage(
        this.normalizeToValue(header, message, ...args)
      );

      console.groupCollapsed(...messageGroups);

      content.forEach(([key, item]) => {
        console.log(key, this.normalizeToValue(item, message, ...args));
      });

      console.groupEnd();
    };
  }

  protected static normalizeToValue<Value, Args extends unknown[]>(
    funcOrValue: ((...args: Args) => Value) | Value | undefined,
    ...args: Args
  ) {
    if (funcOrValue === undefined) {
      return undefined as Value extends undefined ? undefined : Value;
    }

    return (
      typeof funcOrValue === 'function'
        ? (funcOrValue as (...args: Args) => Value)(...args)
        : funcOrValue
    ) as Value extends undefined ? undefined : Value;
  }
}
